'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { Suspense, useEffect, useState } from 'react';
import cookie from 'react-cookies';

import Intercom from '@/components/Intercom';
import LayoutLoading from '@/components/layoutLoading';
import ProviderGeniusDetail from '@/context/GeniusDetail';
import { useRegion } from '@/hooks/useAppEnv';
import { useChatManager } from '@/hooks/useChatManager';
import { useModel } from '@/hooks/useModel';
import Layouts from '@/layouts/PA-Layout';
import { EGlobalCookieKey } from '@/types/cookie';
import { isTrulyProd } from '@/utils/host';
import { ROUTER_PATH, generateParams } from '@/utils/router';

interface IProps {
  children: React.ReactNode;
}

// Script wrapper component
const ScriptWrapper = ({ children }: { children: React.ReactNode }) => (
  <>
    <Script
      id="featurebase-sdk"
      src="https://do.featurebase.app/js/sdk.js"
      strategy="afterInteractive"
    />
    <Intercom />
    {children}
  </>
);

function Layout(props: IProps): JSX.Element | React.ReactNode | null {
  const { children } = props;
  const pathName = usePathname();
  const router = useRouter();
  const token = cookie.load(EGlobalCookieKey.token);
  const searchParams = useSearchParams();
  const [autoCreate, setAutoCreate] = useState(false);
  const { region } = useRegion(); // 'cn' | 'intl'

  useEffect(() => {
    if (pathName !== ROUTER_PATH.personalAI) return;

    if (!token) {
      if (isTrulyProd()) {
        const target =
          region === 'cn'
            ? 'https://intro.second-me.cn?utm_campaign=appRedirect'
            : 'https://home.second.me?utm_campaign=appRedirect';

        // 跳外站建议用 window.location，离开 SPA 更干净
        window.location.replace(target);
      } else {
        router.push(ROUTER_PATH.login);
      }
    }
  }, [pathName, region, router, token]);

  const { isLogin, mindDetail } = useModel('auth-model', (model) => ({
    isLogin: model.isLogin,
    mindDetail: model.mindDetail
  }));

  // 初始化 WebSocket 以接收系统通知
  useChatManager();

  useEffect(() => {
    setAutoCreate(sessionStorage.getItem('autoCreatePA') === 'true');
  }, []);

  useEffect(() => {
    if (
      pathName === ROUTER_PATH.personalAICreate &&
      !!mindDetail &&
      !autoCreate &&
      !searchParams.get('reset')
    ) {
      router.replace(ROUTER_PATH.personalAI);
    }
  }, [mindDetail, pathName, router, autoCreate, searchParams]);

  useEffect(() => {
    if (isLogin === false && pathName !== ROUTER_PATH.personalAI && !searchParams.get('isLogout')) {
      searchParams.has('redirect')
        ? null
        : router.replace(
            generateParams(ROUTER_PATH.login, {
              redirect: encodeURIComponent(window.location.href),
              ...(searchParams.get('utm_source') && {
                utm_source: searchParams.get('utm_source') || undefined
              }),
              ...(searchParams.get('utm_medium') && {
                utm_medium: searchParams.get('utm_medium') || undefined
              }),
              ...(searchParams.get('utm_campaign') && {
                utm_campaign: searchParams.get('utm_campaign') || undefined
              })
            })
          );
    }
  }, [isLogin, router, searchParams, pathName]);

  if (pathName === ROUTER_PATH.personalAICreate) {
    if (!mindDetail || autoCreate || searchParams.get('reset')) {
      return <ScriptWrapper>{children}</ScriptWrapper>;
    }

    return <ScriptWrapper>{null}</ScriptWrapper>; // 等待上面的重定向
  }

  return (
    <ScriptWrapper>
      <ProviderGeniusDetail detailInfo={mindDetail || {}}>
        <Suspense>{token ? <Layouts type="pa">{children}</Layouts> : <LayoutLoading />}</Suspense>
      </ProviderGeniusDetail>
    </ScriptWrapper>
  );
}

export default Layout;
